import React, { useContext, useRef } from 'react';
import { useIntl } from 'react-intl';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography/Typography';
import {
  ApiFilterOperation,
  DetailContext,
  DialogHandle,
  DictionaryFieldDialog,
  FieldFilter,
  TableFieldCells,
  useScrollableSource,
} from '@eas/common-web';
import { CreatedByCell } from '@components/evidence/authored-evidence/created-by-cell';
import { useDraftsVisibility } from '@modules/es-file/es-file-permission';
import { Draft } from '@models';
import { EvidenceApiUrl } from '@enums';

export function SelectFromDraftsButton({
  disabled,
  onDraftSelect,
  multiple = false,
  esFileId,
}: {
  disabled: boolean;
  onDraftSelect: (draft: Draft | Draft[]) => void;
  multiple?: boolean;
  esFileId?: string;
}) {
  const { source, isLocked } = useContext(DetailContext);
  const draftEsFile = esFileId ? { id: esFileId } : source.data?.esFile;
  const { canViewDrafts } = useDraftsVisibility(draftEsFile);

  const dialogRef = useRef<DialogHandle>(null);
  const drafts = useScrollableSource<Draft>({
    url: `${EvidenceApiUrl.ES_FILES}/${
      esFileId ?? source.data?.esFile?.id
    }/draft/list`,
  });
  const intl = useIntl();

  if (!canViewDrafts) {
    return null;
  }

  return (
    <>
      <Button
        disabled={isLocked || disabled}
        style={{ minWidth: 150, height: 23, padding: 2, margin: '0 4px' }}
        title="Vybrat z konceptů"
        onClick={() => dialogRef.current?.open()}
        color="primary"
        variant="contained"
      >
        <Typography variant="body2">
          {intl.formatMessage({
            id: 'ES__DOCUMENTS__ACTIONBAR__SEARCH_DRAFT',
            defaultMessage: 'Vybrat z konceptů',
          })}
        </Typography>
      </Button>
      <DictionaryFieldDialog
        ref={dialogRef}
        columns={[
          {
            name: intl.formatMessage({
              id: 'ES__DRAFTS__COLUMN__NAME',
              defaultMessage: 'Název konceptu',
            }),
            datakey: 'name',
            width: 200,
          },
          {
            name: intl.formatMessage({
              id: 'ES__DRAFTS__COLUMN__FILE_NAME',
              defaultMessage: 'Název souboru',
            }),
            datakey: 'file.name',
            width: 200,
          },
          {
            name: intl.formatMessage({
              id: 'ES__DRAFTS__COLUMN__CREATED_BY',
              defaultMessage: 'Tvůrce',
            }),
            datakey: 'createdBy.name',
            width: 200,
            CellComponent: CreatedByCell,
          },
          {
            name: intl.formatMessage({
              id: 'ES__DRAFTS__COLUMN__UPDATED',
              defaultMessage: 'Datum editace',
            }),
            datakey: 'updated',
            width: 200,
            CellComponent: TableFieldCells.DateCell,
          },
        ]}
        filtersFields={[
          {
            label: intl.formatMessage({
              id: 'ES__DRAFTS__COLUMN__NAME',
              defaultMessage: 'Název',
            }),
            datakey: 'name',
            filterkey: 'name',
            defaultOperation: ApiFilterOperation.CONTAINS,
            FilterComponent: FieldFilter.FilterTextCell,
          },
        ]}
        onChange={onDraftSelect}
        width={1000}
        source={drafts}
        multiple={multiple}
      />
    </>
  );
}
